<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { picoSearch } from '@scmmishra/pico-search';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';

defineOptions({ name: 'WhatsappGroupsList' });

const store = useStore();
const getters = useStoreGetters();
const router = useRouter();
const { t } = useI18n();
const { accountId } = useAccount();

const searchQuery = ref('');

const groups = computed(() => getters['whatsappGroups/getGroups'].value);
const uiFlags = computed(() => getters['whatsappGroups/getUIFlags'].value);

const filteredGroups = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return groups.value;
  return picoSearch(groups.value, query, ['name']);
});

const comConversa = computed(
  () => groups.value.filter(g => g.conversation_display_id).length
);

onMounted(() => store.dispatch('whatsappGroups/get'));

const abrirGrupo = group => {
  if (!group.conversation_display_id) {
    useAlert(t('WHATSAPP_GROUPS.NO_CONVERSATION'));
    return;
  }
  // o param e conversation_id em snake_case; camelCase lanca MissingRequiredParam
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: group.conversation_display_id,
    },
  });
};

const sincronizar = async () => {
  try {
    await store.dispatch('whatsappGroups/sync');
    useAlert(t('WHATSAPP_GROUPS.SYNC_STARTED'));
    // a sincronizacao leva ~40s no servidor; recarrega depois de um tempo
    setTimeout(() => store.dispatch('whatsappGroups/get'), 45000);
  } catch (error) {
    useAlert(t('WHATSAPP_GROUPS.SYNC_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header class="flex items-center justify-between gap-4 px-6 py-4">
      <div class="flex flex-col gap-1 min-w-0">
        <h1 class="text-heading-2 text-n-slate-12">
          {{ t('WHATSAPP_GROUPS.HEADER') }}
        </h1>
        <p class="text-body-main text-n-slate-11">
          {{
            t('WHATSAPP_GROUPS.COUNT', {
              total: groups.length,
              active: comConversa,
            })
          }}
        </p>
      </div>

      <div class="flex items-center gap-2 flex-shrink-0">
        <input
          v-model="searchQuery"
          type="text"
          class="w-56 !mb-0"
          :placeholder="t('WHATSAPP_GROUPS.SEARCH')"
        />
        <Button
          :label="t('WHATSAPP_GROUPS.SYNC')"
          icon="i-lucide-refresh-cw"
          size="sm"
          :is-loading="uiFlags.isSyncing"
          :disabled="uiFlags.isSyncing"
          @click="sincronizar"
        />
      </div>
    </header>

    <div class="flex-1 px-6 pb-6 overflow-y-auto">
      <div v-if="uiFlags.isFetching" class="flex justify-center py-12">
        <Spinner />
      </div>

      <div
        v-else-if="!filteredGroups.length"
        class="flex flex-col items-center gap-2 py-16 text-center"
      >
        <p class="text-body-main text-n-slate-11">
          {{
            groups.length
              ? t('WHATSAPP_GROUPS.NO_RESULTS')
              : t('WHATSAPP_GROUPS.EMPTY')
          }}
        </p>
        <Button
          v-if="!groups.length"
          :label="t('WHATSAPP_GROUPS.SYNC')"
          size="sm"
          :is-loading="uiFlags.isSyncing"
          @click="sincronizar"
        />
      </div>

      <div v-else class="grid gap-2">
        <button
          v-for="group in filteredGroups"
          :key="group.id"
          class="flex items-center w-full gap-3 px-4 py-3 text-left transition-colors border rounded-lg cursor-pointer border-n-weak bg-n-solid-1 hover:bg-n-slate-2"
          @click="abrirGrupo(group)"
        >
          <Avatar :name="group.name" :src="group.thumbnail" :size="32" />

          <div class="flex flex-col min-w-0 flex-1">
            <span class="truncate text-heading-3 text-n-slate-12">
              {{ group.name }}
            </span>
            <span class="text-body-small text-n-slate-11">
              <template v-if="group.size">
                {{ t('WHATSAPP_GROUPS.MEMBERS', { count: group.size }) }}
              </template>
              <template v-if="group.announce_only">
                · {{ t('WHATSAPP_GROUPS.ANNOUNCE_ONLY') }}
              </template>
            </span>
          </div>

          <span
            v-if="group.conversation_display_id"
            class="px-2 py-1 text-xs rounded-md flex-shrink-0 bg-n-teal-3 text-n-teal-11"
          >
            {{ t('WHATSAPP_GROUPS.HAS_CONVERSATION') }}
          </span>
          <span
            v-else
            class="px-2 py-1 text-xs rounded-md flex-shrink-0 bg-n-slate-3 text-n-slate-11"
          >
            {{ t('WHATSAPP_GROUPS.NO_ACTIVITY') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
